import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import { baseUrl } from '../config';
import { Loader2, Calendar, Clock, Utensils, FileText, Plus, X, Trash2 } from 'lucide-react';

const createAuthAxios = () => {
  const token = localStorage.getItem('access_token');
  return axios.create({
    baseURL: baseUrl,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
};

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

const EnthusiastSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    date: '', day: '', work_hours: '', meal_hours: '', task_description: '', file_url: ''
  });

  const fetchSchedules = async () => {
    setLoading(true);
    setError('');
    try {
      const api = createAuthAxios();
      const res = await api.get('/enthusiast/schedule');
      setSchedules(res.data || []);
    } catch (err) {
      setError('Failed to load schedules.');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDone = async (scheduleId) => {
    setDeletingId(scheduleId);
    try {
      const api = createAuthAxios();
      await api.delete(`/enthusiast/schedule/${scheduleId}`);
      setSchedules(prev => prev.filter(s => s.schedule_id !== scheduleId));
    } catch (err) {
      setError('Failed to update schedule.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const api = createAuthAxios();
      const res = await api.post('/enthusiast/schedule', form);
      setSchedules(prev => [...prev, res.data]);
      setShowAddForm(false);
      setForm({ date: '', day: '', work_hours: '', meal_hours: '', task_description: '', file_url: '' });
    } catch (err) {
      setError('Failed to add schedule.');
    } finally {
      setAdding(false);
    }
  };

  // Auto-fill day when date is picked
  const handleDateChange = (dateStr) => {
    const dayName = dateStr ? DAYS[new Date(dateStr).getDay()] : '';
    setForm(prev => ({ ...prev, date: dateStr, day: dayName || prev.day }));
  };

  useEffect(() => { fetchSchedules(); }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-teal-600" size={40} />
        <span className="ml-3 text-gray-600 font-medium">Loading schedules...</span>
      </div>
    );
  }

  return (
    <div className="p-4">
      <SEO
        title="My Film Schedule"
        description="View and manage your personal film production schedule on ProducerBuddy."
        url="https://producerbuddy.pilomation.com/enthusiast-schedule"
        robots="noindex, nofollow"
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Calendar className="text-teal-600" size={28} /> My Schedule
        </h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-teal-700 transition-colors shadow"
        >
          <Plus size={18} /> Add Schedule
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-900 rounded font-medium">{error}</div>
      )}

      {schedules.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border-2 border-teal-100 shadow">
          <Calendar className="mx-auto mb-3 text-teal-400" size={48} />
          <p className="text-xl font-bold text-gray-900">No schedules yet</p>
          <p className="text-gray-500 mt-1 text-sm">Add your first schedule entry above.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl shadow-lg border-2 border-teal-100">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-gradient-to-r from-teal-500 to-teal-600 text-white">
                {['Date','Day','Work Hours','Meal Hours','Task','File','Actions'].map(h => (
                  <th key={h} className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {schedules.map((s, i) => (
                <tr key={s.schedule_id} className={i % 2 === 0 ? 'bg-white hover:bg-teal-50' : 'bg-gray-50 hover:bg-teal-50'}>
                  <td className="px-6 py-4 text-gray-900 font-medium">{s.date}</td>
                  <td className="px-6 py-4 text-gray-700">{s.day || '—'}</td>
                  <td className="px-6 py-4 text-gray-700">{s.work_hours || '—'}</td>
                  <td className="px-6 py-4 text-gray-700">{s.meal_hours || '—'}</td>
                  <td className="px-6 py-4 text-gray-700">{s.task_description || '—'}</td>
                  <td className="px-6 py-4">
                    {s.file_url
                      ? <a href={s.file_url} target="_blank" rel="noopener noreferrer"
                          className="text-teal-600 hover:text-teal-800 font-semibold underline">View</a>
                      : <span className="text-gray-400 italic text-sm">No file</span>}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleMarkDone(s.schedule_id)}
                      disabled={deletingId === s.schedule_id}
                      className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 font-semibold text-sm shadow transition-colors"
                    >
                      {deletingId === s.schedule_id
                        ? <><Loader2 size={14} className="animate-spin" /> Updating...</>
                        : <><Trash2 size={14} /> Done</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Schedule Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl border-2 border-teal-200 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add Schedule Entry</h3>
              <button onClick={() => setShowAddForm(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddSchedule} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Date</label>
                <input type="date" value={form.date}
                  onChange={(e) => handleDateChange(e.target.value)} required
                  className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Day</label>
                <input type="text" value={form.day}
                  onChange={(e) => setForm({ ...form, day: e.target.value })}
                  placeholder="Auto-filled from date"
                  className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Work Hours</label>
                <input type="text" value={form.work_hours}
                  onChange={(e) => setForm({ ...form, work_hours: e.target.value })}
                  placeholder="e.g. 9:00 AM - 5:00 PM"
                  className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Meal Hours</label>
                <input type="text" value={form.meal_hours}
                  onChange={(e) => setForm({ ...form, meal_hours: e.target.value })}
                  placeholder="e.g. 1:00 PM - 2:00 PM"
                  className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Task Description</label>
                <textarea value={form.task_description}
                  onChange={(e) => setForm({ ...form, task_description: e.target.value })}
                  rows={3} placeholder="What's the task for this day?"
                  className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={adding}
                  className="flex-1 py-2 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {adding ? <><Loader2 size={16} className="animate-spin" /> Adding...</> : 'Add Schedule'}
                </button>
                <button type="button" onClick={() => setShowAddForm(false)}
                  className="flex-1 py-2 bg-gray-100 text-gray-800 rounded-lg font-bold border-2 border-gray-300">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnthusiastSchedule;
